import { IconBadgeTone, ITorqIconBadgeTone } from './torqTypes';

// Escala de radius do Design System TORQ (guia seção 29).
export const radii = {
  xs: 6,
  sm: 8,
  md: 10,
  lg: 12,
  xl: 16,
  pill: 999,
};

// Paleta oficial do SIAC: ciano como identidade, azul-petróleo no menu e
// verde/azul/amarelo/vermelho só com função semântica.
//
// Os tons `*Button`/`primaryDark` são o MESMO matiz em versão escura: é o que
// se usa quando a cor vira TEXTO sobre fundo claro (o tom puro não tem
// contraste, ex.: #42F2F7 sobre branco dá 1,4:1). No tema, ficam em
// `palette.<cor>.dark`.
export const brand = {
  primary: '#42F2F7',
  primaryHover: '#1FDDE3',
  primaryDark: '#0B7C86',
  primaryLight: '#A6F8FB',
  menuBase: '#062D3A',
  menuSecondary: '#083D4A',
  cyan: '#42F2F7',
  success: '#48C62F',
  successButton: '#27801A',
  warning: '#FFB90A',
  warningButton: '#946800',
  error: '#FF545F',
  errorButton: '#CF2330',
  info: '#287EFF',
  infoButton: '#1A5FCC',
  purple: '#5B7FA6',
};

// Tema escuro — azul-petróleo muito escuro, superfícies azul-acinzentadas e
// textos claros com leve tom do ciano.
export const darkColors = {
  page: '#06202A',
  sidebar: brand.menuBase,
  level2: '#0C2C38',
  card: '#123340',
  elevated: '#173D4B',

  borderDefault: 'rgba(168, 192, 205, 0.14)',
  borderStrong: 'rgba(168, 192, 205, 0.24)',
  borderFocus: brand.primary,

  textPrimary: '#E3F3F6',
  textSecondary: '#A3C3CC',
  textMuted: '#7A9AA5',
  textDisabled: 'rgba(163, 195, 204, 0.45)',

  hover: 'rgba(66, 242, 247, 0.06)',
  selected: 'rgba(66, 242, 247, 0.14)',

  scrollbarThumb: 'rgba(168, 192, 205, 0.24)',
  scrollbarThumbHover: 'rgba(168, 192, 205, 0.36)',

  cardShadow: '0 1px 3px rgba(0, 0, 0, 0.24)',
  dialogShadow: '0 12px 32px rgba(0, 0, 0, 0.36)',
};

// Tema claro — fundo cinza-azulado, superfícies brancas e textos com leve tom
// do ciano primary (matiz petróleo, não cinza neutro).
export const lightColors = {
  page: '#F3F6FA',
  paper: '#FFFFFF',
  level2: '#F7F9FC',
  card: '#FFFFFF',
  elevated: '#FFFFFF',

  borderDefault: '#E2EAF0',
  borderStrong: '#D0DDE5',
  borderFocus: brand.primary,
  divider: '#E2EAF0',

  textPrimary: '#17343F',
  textSecondary: '#557480',
  textMuted: '#86A0AA',
  textDisabled: 'rgba(85, 116, 128, 0.45)',

  hover: 'rgba(66, 242, 247, 0.08)',
  selected: '#E2FBFC',

  scrollbarThumb: 'rgba(85, 116, 128, 0.22)',
  scrollbarThumbHover: 'rgba(85, 116, 128, 0.34)',

  cardShadow: '0 1px 3px rgba(6, 45, 58, 0.06)',
  dialogShadow: '0 12px 32px rgba(6, 45, 58, 0.14)',
};

// Fundo suave do IconBadge por tom — translúcido, serve nos dois temas.
const tomBadge = (rgb: string, iconColor: string): ITorqIconBadgeTone => ({
  gradient: `rgba(${rgb},.14)`,
  border: `rgba(${rgb},.28)`,
  iconColor,
});

export const iconBadgeTones: Record<IconBadgeTone, ITorqIconBadgeTone> = {
  primary: tomBadge('66,242,247', '#12A6B0'),
  cyan: tomBadge('66,242,247', '#12A6B0'),
  success: tomBadge('72,198,47', '#3BAE24'),
  error: tomBadge('255,84,95', brand.error),
  warning: tomBadge('255,185,10', '#E6A400'),
  purple: tomBadge('40,126,255', brand.info),
  neutral: tomBadge('85,116,128', '#6F8D99'),
};

// Escala tipográfica nomeada (guia seção 12) — consumida tanto pelo
// `typography` do MUI quanto por componentes que precisem dos mesmos números
// fora de uma variante MUI (ex.: valor de um stat card).
export const typographyScale = {
  pageTitle: { fontSize: '1.375rem', fontWeight: 650 },
  sectionTitle: { fontSize: '1rem', fontWeight: 600 },
  cardTitle: { fontSize: '0.75rem', fontWeight: 500 },
  cardValue: { fontSize: '1.125rem', fontWeight: 650 },
  body: { fontSize: '0.875rem', fontWeight: 400 },
  secondary: { fontSize: '0.8125rem', fontWeight: 400 },
  caption: { fontSize: '0.75rem', fontWeight: 400 },
  button: { fontSize: '0.8125rem', fontWeight: 600 },
  tableHeader: { fontSize: '0.75rem', fontWeight: 600 },
  tableBody: { fontSize: '0.8125rem', fontWeight: 400 },
};

// Array de 25 sombras exigido pelo MUI (Theme['shadows']) — só as primeiras
// entradas são diferenciadas (uso real: dialogs/menus/popovers, guia seção
// 31); o restante repete a sombra "forte" do tema por consistência, em vez
// dos placeholders idênticos que existiam antes.
export const buildShadows = (mode: 'light' | 'dark'): import('@mui/material/styles').Theme['shadows'] => {
  const soft = mode === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(6,45,58,0.06)';
  const strong = mode === 'dark' ? darkColors.dialogShadow : lightColors.dialogShadow;

  const shadows = [
    'none',
    `0 1px 3px ${soft}`,
    `0 2px 6px ${soft}`,
    `0 4px 12px ${soft}`,
    `0 6px 18px ${soft}`,
  ];
  while (shadows.length < 25) shadows.push(strong);

  return shadows as unknown as import('@mui/material/styles').Theme['shadows'];
};
